
import React, { useEffect, useCallback } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Form } from "@/components/ui/form";
import { Users } from "lucide-react";
import { useAddClientDialogNew as useAddClientDialog } from "../hooks/useAddClientDialogNew";
import { IndividualClientForm } from "../forms/IndividualClientForm";
import { LegalEntityClientForm } from "../forms/LegalEntityClientForm";
import { StandardEntityDialog } from "@/components/common/dialogs/StandardEntityDialog";

interface AddClientDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onClientCreated?: (clientId: string) => void;
  preselectedClientId?: string;
}

const AddClientDialog = ({
  open,
  onOpenChange,
  onClientCreated,
  preselectedClientId
}: AddClientDialogProps) => {
  const {
    form,
    clientType,
    isSubmitting,
    onSubmit,
    handleClientTypeChange,
    loadDraft,
    clearDraft
  } = useAddClientDialog({
    onClientCreated,
    onClose: () => onOpenChange(false)
  });

  useEffect(() => {
    if (open) {
      const draftLoaded = loadDraft();
      if (draftLoaded) {
        console.log('Draft loaded from localStorage');
      }
    }
  }, [open, loadDraft]);

  const handleClose = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const handleOpenChange = useCallback((newOpen: boolean) => {
    if (!newOpen && form.formState.isDirty && !isSubmitting) {
      const hasChanges = Object.values(form.getValues()).some(value => 
        typeof value === 'string' ? value !== '' : 
        typeof value === 'boolean' ? value !== false : 
        value !== undefined && value !== null
      );
      if (hasChanges) {
        const confirmed = window.confirm('У вас есть несохраненные изменения. Данные будут сохранены как черновик. Закрыть диалог?');
        if (!confirmed) return;
      }
    }
    onOpenChange(newOpen);
  }, [form, isSubmitting, onOpenChange]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('[AddClientDialog] Submitting form...');
    await onSubmit();
  }, [onSubmit]);

  const validationErrorsArray: string[] = [];

  return (
    <StandardEntityDialog
      icon={<Users className="h-6 w-6" />}
      title="Добавить клиента"
      isOpen={open}
      onClose={handleClose}
      onSubmit={handleSubmit}
      isLoading={isSubmitting}
      validationErrors={validationErrorsArray}
      size="standard"
      saveText={isSubmitting ? "Создание..." : "Создать клиента"}
      cancelText="Отмена"
    >
      <div className="space-y-4 w-full p-4">
        <div>
          <Tabs value={clientType} onValueChange={handleClientTypeChange} defaultValue="individual">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="individual">Физическое лицо</TabsTrigger>
              <TabsTrigger value="legal">Юридическое лицо</TabsTrigger>
            </TabsList>

            <TabsContent value="individual" className="space-y-4 mt-4">
              <Form {...form}>
                <IndividualClientForm form={form} autoFocus={true} />
              </Form>
            </TabsContent>

            <TabsContent value="legal" className="space-y-4 mt-4">
              <Form {...form}>
                <LegalEntityClientForm form={form} autoFocus={true} />
              </Form>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </StandardEntityDialog>
  );
};

export default AddClientDialog;
